import { useEffect, useState } from 'react';

const ScriptLoader = () => {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [loadedScripts, setLoadedScripts] = useState(new Set());

  const createCacheableScript = (src, id, cacheTime) => {
    const script = document.createElement('script');
    script.async = true;
    script.src = `${src}${src.includes('?') ? '&' : '?'}cache=${cacheTime}`;
    script.id = id;
    script.setAttribute('crossorigin', 'anonymous');
    script.setAttribute('referrerpolicy', 'no-referrer');
    return script;
  };

  const loadScript = (src, id, cacheTime) => {
    if (loadedScripts.has(id)) return;

    const script = createCacheableScript(src, id, cacheTime);
    document.head.appendChild(script);
    setLoadedScripts((prev) => new Set([...prev, id]));
  };

  const loadGA = () => {
    const hasGAConsent = document.cookie.includes(
      'gatsby-gdpr-google-analytics=true',
    );
    if (typeof window !== 'undefined' && !window.gaLoaded && hasGAConsent) {
      window.gaLoaded = true;

      // Load GA script with 1 day cache
      loadScript(
        `https://www.googletagmanager.com/gtag/js?id=${process.env.GATSBY_API_GA}`,
        'ga-script',
        '1d',
      );

      window.dataLayer = window.dataLayer || [];
      window.gtag = function (...args) {
        window.dataLayer.push(args);
      };
      window.gtag('js', new Date());
      window.gtag('config', process.env.GATSBY_API_GA, {
        anonymize_ip: true,
        cookie_expires: 0,
        'cache-control': 'public, max-age=86400', // 1 day cache
      });
    }
  };

  const loadGTM = () => {
    const hasGTMConsent = document.cookie.includes(
      'gatsby-gdpr-google-tagmanager=true',
    );
    if (typeof window !== 'undefined' && !window.gtmLoaded && hasGTMConsent) {
      window.gtmLoaded = true;

      // Load GTM script with 2 hour cache
      loadScript(
        `https://www.googletagmanager.com/gtm.js?id=${process.env.GATSBY_API_GTM}`,
        'gtm-script',
        '2h',
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
        'cache-control': 'public, max-age=7200', // 2 hours cache
      });
    }
  };

  const loadGoogleTranslate = () => {
    if (typeof window !== 'undefined' && !window.googleTranslateLoaded) {
      window.googleTranslateLoaded = true;
      loadScript(
        '//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit',
        'translate-script',
        '1d',
      );
    }
  };

  const handleInteraction = () => {
    if (!hasInteracted) {
      setHasInteracted(true);
      loadGA();
      loadGTM();
      loadGoogleTranslate();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const events = ['mousemove', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      document.addEventListener(event, handleInteraction, { once: true });
    });

    // Listen for consent changes
    const consentObserver = () => {
      if (hasInteracted) {
        loadGA();
        loadGTM();
      }
    };
    document.addEventListener(
      'gatsby-plugin-gdpr-cookies-loaded',
      consentObserver,
    );

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleInteraction);
      });
      document.removeEventListener(
        'gatsby-plugin-gdpr-cookies-loaded',
        consentObserver,
      );
    };
  }, [hasInteracted]);

  return null;
};

export default ScriptLoader;
